import React, { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { MemoizedClassicHeader } from '@/components/features/ClassicHeader';
import { MemoizedFooter } from '@/components/features/Footer';
import { MemoizedHeader } from '@/components/features/Header';
import { ButtonAnchor, Notifications } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { PageType } from '@/generated/data.schemas';
import { useGetLayout } from '@/generated/layout';
import { useGetTranslations } from '@/generated/translations';
import { portalRootId } from '@/helpers';
import { OverlayTypes, useOverlayStore } from '@/store/overlayStore';
import styles from './_page.partial.module.scss';

interface PageProps {
	isModal?: boolean;
	navModifier?: 'critical' | 'none';
	largeContentSpacer?: boolean;
	smallContentSpacer?: boolean;
	hasNoContentSpacer?: boolean;
	hideCustomerSelector?: boolean;
	disableQuickBasket?: boolean;
	children?: React.ReactNode;
	pageType?: PageType;
	hasStickyHeader?: boolean;
	hasClassicHeader?: boolean;
}

const Page: React.FunctionComponent<PageProps> = ({
	isModal,
	navModifier,
	disableQuickBasket,
	hideCustomerSelector,
	largeContentSpacer,
	smallContentSpacer,
	hasStickyHeader,
	hasClassicHeader,
	children,
	pageType,
}) => {
	const { segmentationId, url: pageOrigin } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: layout } = useGetLayout(segmentationId);
	const showOverlay = useOverlayStore((state) => state.showOverlay);
	const location = useLocation();

	const searchUrl = layout?.data.staticLinks.find((link) => link.pageType === PageType.Search)?.url || '';

	const mainContentRef = useRef<HTMLDivElement>(null);
	const mainContentId = 'main-content';

	useEffect(() => {
		if (location?.state?.preventScrollTo) {
			return;
		}

		if (location.hash && location.hash.includes('#')) {
			if (mainContentRef.current) {
				setTimeout(() => {
					const anchor = mainContentRef.current?.querySelector(location.hash);
					if (anchor) {
						anchor.scrollIntoView({ block: 'start' });
					}
				}, 100);
			}
		}
	}, [pageType, location, mainContentRef]);

	const structuredData = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		url: pageOrigin,
		potentialAction: {
			'@type': 'SearchAction',
			target: `${pageOrigin}${searchUrl}?phrase={query}`,
			'query-input': 'required maxlength=255 name=query',
		},
	};

	if (isModal) {
		return <div>{children}</div>;
	}

	return (
		<>
			<ButtonAnchor className={styles.skipLink} href={`#${mainContentId}`} target="_self">
				{translations?.data.shared.skipToMainContent}
			</ButtonAnchor>

			<div
				className={classNames('u-overlay', {
					['isShown']: showOverlay !== false,
					['isFullOverlay']: showOverlay === OverlayTypes.FULL,
				})}
			/>
			{navModifier !== 'none' && (
				<>
					{hasClassicHeader ? (
						<MemoizedClassicHeader
							disableQuickBasket={disableQuickBasket}
							layout={layout?.data}
							translations={translations?.data}
							isSticky={hasStickyHeader}
						/>
					) : (
						<MemoizedHeader
							hideCustomerSelector={hideCustomerSelector}
							critical={navModifier === 'critical'}
							disableQuickBasket={disableQuickBasket}
							layout={layout?.data}
							translations={translations?.data}
							isSticky={hasStickyHeader}
						/>
					)}
				</>
			)}

			<main
				className={classNames(styles.contentWrapper, {
					[styles.hasLargeSpacer]: largeContentSpacer,
					[styles.hasSmallSpacer]: smallContentSpacer,
					[styles.hasTopMargin]: hasStickyHeader,
					[styles.noStickyHeader]: !hasStickyHeader,
				})}
				id={mainContentId}
				ref={mainContentRef}
			>
				{children}
			</main>
			{navModifier !== 'none' && (
				<MemoizedFooter
					critical={navModifier === 'critical'}
					layout={layout?.data}
					translations={translations?.data}
				/>
			)}

			<Notifications />
			<div id={portalRootId} />

			<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
		</>
	);
};

export default Page;
